import type {
  LiveReactionEmojiType,
  SendLiveReactionsMutationVariablesType,
} from "@/types/graphql/site.generated";
import { DEFAULT_LIVE_REACTION_EMOJIS } from "./reactionEmoji";

export type LocalReaction =
  | { kind: "emoji"; type: LiveReactionEmojiType }
  | { kind: "phrase"; index: number };

export interface ReactionBatch {
  emojis: Partial<Record<LiveReactionEmojiType, number>>;
  phraseIndexes: number[];
}

/**
 * Нажатия копятся 1,5 с и уходят одной мутацией (спека, «Отправка»).
 * Счётчики зрителям и так приходят пачкой раз в секунду, а вызовов
 * sendLiveReactions втрое меньше, чем при 0,5 с (спека viewer-db-load, 5).
 */
export const SEND_DELAY_MS = 1500;
/** Те же пределы, что проверяет сервер: 1..10 на тип, до 3 фраз. */
export const MAX_COUNT_PER_TYPE = 10;
export const MAX_PHRASES_PER_CALL = 3;

export const emptyBatch = (): ReactionBatch => ({
  emojis: {},
  phraseIndexes: [],
});

export function addToBatch(
  batch: ReactionBatch,
  reaction: LocalReaction,
): ReactionBatch {
  if (reaction.kind === "emoji") {
    const current = batch.emojis[reaction.type] ?? 0;
    if (current >= MAX_COUNT_PER_TYPE) return batch;
    return {
      ...batch,
      emojis: { ...batch.emojis, [reaction.type]: current + 1 },
    };
  }
  if (batch.phraseIndexes.length >= MAX_PHRASES_PER_CALL) return batch;
  return { ...batch, phraseIndexes: [...batch.phraseIndexes, reaction.index] };
}

/**
 * Переменные мутации или null, если отправлять нечего. Индексы фраз и
 * эмодзи, которых уже нет у платформы (партнёр сменил набор, пока страница
 * открыта), выбрасываются: сервер всё равно отклонил бы весь вызов.
 * Порядок эмодзи в вызове это порядок набора платформы.
 */
export function toSendVariables(
  contentId: string,
  batch: ReactionBatch,
  phrasesCount: number,
  allowedEmojis: readonly LiveReactionEmojiType[] = DEFAULT_LIVE_REACTION_EMOJIS,
): SendLiveReactionsMutationVariablesType | null {
  const emojis = allowedEmojis
    .map((type) => ({ type, count: batch.emojis[type] ?? 0 }))
    .filter((emoji) => emoji.count > 0);
  const phraseIndexes = batch.phraseIndexes.filter(
    (index) => Number.isInteger(index) && index >= 0 && index < phrasesCount,
  );

  if (emojis.length === 0 && phraseIndexes.length === 0) return null;
  return { contentId, emojis, phraseIndexes };
}
